/** ProductividadTab — Monitoreo de casos (tickets) por antigüedad en días hábiles.
 *
 * Filtros: Encargado (responsable/resolutor), Estado (terminados/abiertos),
 * Fechas (todo/mes/rango) y Responsable específico.
 * Muestra 4 cards por banda + tabla tabular (persona x días hábiles) + export xlsx.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Loader2, Download, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const BAND_STYLES = {
  verde:   { card: 'border-l-4 border-l-emerald-500', chip: 'text-emerald-700', head: 'bg-emerald-100 text-emerald-800' },
  ambar:   { card: 'border-l-4 border-l-amber-400',   chip: 'text-amber-700',   head: 'bg-amber-100 text-amber-800' },
  naranja: { card: 'border-l-4 border-l-orange-500',  chip: 'text-orange-700',  head: 'bg-orange-100 text-orange-800' },
  rojo:    { card: 'border-l-4 border-l-red-500',     chip: 'text-red-700',     head: 'bg-red-100 text-red-800' },
};

// Color de banda por número de columna (día hábil).
function bandOfCol(col) {
  if (col === '16+') return 'rojo';
  const n = parseInt(col, 10);
  if (n <= 5) return 'verde';
  if (n <= 10) return 'ambar';
  if (n <= 15) return 'naranja';
  return 'rojo';
}
function cellBg(col) {
  return { verde: 'bg-emerald-50', ambar: 'bg-amber-50', naranja: 'bg-orange-50', rojo: 'bg-red-50' }[bandOfCol(col)];
}

export default function ProductividadTab() {
  const { api, empresa } = useAuth();
  const [encargado, setEncargado] = useState('responsable');
  const [estado, setEstado] = useState('terminados');
  const [fecha, setFecha] = useState('todo');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [responsableId, setResponsableId] = useState('__todos__');
  const [personas, setPersonas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(() => {
    const params = { encargado, estado, fecha };
    if (fecha === 'rango') { if (desde) params.desde = desde; if (hasta) params.hasta = hasta; }
    if (responsableId && responsableId !== '__todos__') params.responsable_id = responsableId;
    return params;
  }, [encargado, estado, fecha, desde, hasta, responsableId]);

  const fetchPersonas = useCallback(async (signal) => {
    try {
      const r = await api.get('/tickets/bi/responsables', { params: { tipo: encargado }, signal });
      setPersonas(r.data || []);
    } catch { setPersonas([]); }
  }, [api, encargado]);

  const fetchData = useCallback(async (signal) => {
    if (!empresa?.modulo_tickets_activo) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get('/tickets/bi/productividad', { params: buildParams(), signal });
      setData(r.data);
    } catch (e) {
      if (isCanceled(e)) return;
      console.error('Error productividad:', e);
      setData(null);
    } finally { setLoading(false); }
  }, [api, empresa, buildParams]);

  // Al cambiar de encargado, refrescar lista de personas y limpiar selección.
  useAbortableLoad((signal) => { fetchPersonas(signal); setResponsableId('__todos__'); }, [encargado]);
  useAbortableLoad((signal) => fetchData(signal), [encargado, estado, fecha, desde, hasta, responsableId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/tickets/bi/productividad/export', { params: buildParams(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      const now = new Date();
      const p = (n) => String(n).padStart(2, '0');
      const ts = `${p(now.getDate())}.${p(now.getMonth() + 1)}.${now.getFullYear()}.${p(now.getHours())}${p(now.getMinutes())}`;
      a.href = url;
      a.download = `Estado_Tickets_SAC_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exportando:', e);
      alert('No se pudo exportar. Verifica que la vista vista_tickets_bi exista en la base de datos.');
    } finally { setExporting(false); }
  };

  const columnas = data?.tabla?.columnas || [];
  const filas = data?.tabla?.filas || [];
  const totalGen = data?.tabla?.total_general;
  const total = data?.total || 0;

  const nombreEncargadoCol = encargado === 'resolutor' ? 'Resolutor interno' : 'Técnico';

  const cardsUI = useMemo(() => (data?.cards || []).map((c) => {
    const st = BAND_STYLES[c.color] || BAND_STYLES.verde;
    return (
      <Card key={c.key} className={`${st.card} shadow-sm`} data-testid={`prod-card-${c.key}`}>
        <CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{c.label}</div>
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-bold ${st.chip}`} data-testid={`prod-card-count-${c.key}`}>{c.count}</span>
            <span className={`text-sm font-medium ${st.chip}`}>{c.pct}%</span>
          </div>
        </CardContent>
      </Card>
    );
  }), [data]);

  return (
    <div className="space-y-8">
      {/* Filtros */}
      <div className="rounded-xl border border-[#d9d2c4] bg-[#f3f0e9] p-4" data-testid="prod-filtros">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Encargado</Label>
            <Select value={encargado} onValueChange={setEncargado}>
              <SelectTrigger data-testid="prod-encargado" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="responsable">Responsable principal</SelectItem>
                <SelectItem value="resolutor">Resolutor interno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Estado del ticket</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger data-testid="prod-estado" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="terminados">Terminados</SelectItem>
                <SelectItem value="abiertos">Abiertos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Fechas</Label>
            <Select value={fecha} onValueChange={setFecha}>
              <SelectTrigger data-testid="prod-fecha" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todo">Todo</SelectItem>
                <SelectItem value="mes">Mes actual</SelectItem>
                <SelectItem value="rango">Rango de fechas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">
              {encargado === 'resolutor' ? 'Resolutor específico' : 'Responsable específico'}
            </Label>
            <Select value={responsableId} onValueChange={setResponsableId}>
              <SelectTrigger data-testid="prod-responsable" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todos__">Todos</SelectItem>
                {personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {fecha === 'rango' && (
          <div className="flex items-center gap-2 mt-3" data-testid="prod-rango">
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-[160px] bg-white" data-testid="prod-desde" />
            <span className="text-xs text-slate-400">→</span>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-[160px] bg-white" data-testid="prod-hasta" />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button size="sm" variant="ghost" onClick={() => fetchData()} data-testid="prod-refrescar" className="text-slate-600">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          <Button size="sm" onClick={handleExport} disabled={exporting || !total}
            data-testid="prod-exportar"
            style={{ backgroundColor: '#4E6151' }} className="text-white hover:opacity-90">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {exporting ? 'Exportando…' : 'Exportar a Excel'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="prod-error">
          No se pudieron cargar los datos. Asegúrate de que la vista <code>vista_tickets_bi</code> exista en Supabase.
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="prod-cards">{cardsUI}</div>

          {/* Tabla tabular */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#4E6151]" />
                <span className="text-sm font-semibold text-slate-700">Monitoreo de casos · Días hábiles de respuesta</span>
                <span className="text-xs text-slate-400 ml-auto">Total: {total}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="prod-tabla">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-[#4b5563] text-white text-left px-3 py-2 font-medium">{nombreEncargadoCol}</th>
                      {columnas.map((col) => (
                        <th key={col} className={`px-2 py-2 text-center font-semibold ${BAND_STYLES[bandOfCol(col)].head}`} data-testid={`prod-col-${col}`}>
                          {col}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-center font-medium bg-slate-200 text-slate-700">TOTAL</th>
                      <th className="px-3 py-2 text-center font-medium bg-slate-200 text-slate-700">TOTAL %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 ? (
                      <tr><td colSpan={columnas.length + 3} className="text-center text-slate-400 italic py-8">Sin datos para los filtros seleccionados.</td></tr>
                    ) : filas.map((f) => (
                      <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`prod-fila-${f.id}`}>
                        <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{f.nombre}</td>
                        {columnas.map((col) => (
                          <td key={col} className={`px-2 py-2 text-center tabular-nums ${f.dias[col] ? cellBg(col) : ''} ${f.dias[col] ? 'text-slate-800' : 'text-slate-300'}`}>
                            {f.dias[col] || ''}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-center tabular-nums font-semibold bg-slate-50">{f.total}</td>
                        <td className="px-3 py-2 text-center tabular-nums font-medium text-slate-600 bg-slate-50">{f.total_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                  {filas.length > 0 && totalGen && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-[#4b5563] text-white font-semibold" data-testid="prod-total-general">
                        <td className="sticky left-0 z-10 bg-[#4b5563] px-3 py-2 text-white">Total general</td>
                        {columnas.map((col) => (
                          <td key={col} className="px-2 py-2 text-center tabular-nums text-white">{totalGen.dias[col] || ''}</td>
                        ))}
                        <td className="px-3 py-2 text-center tabular-nums text-white">{totalGen.total}</td>
                        <td className="px-3 py-2 text-center tabular-nums text-white">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
